import React from 'react';
import { X } from 'lucide-react';

interface PwaHowToInstallModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PwaHowToInstallModal: React.FC<PwaHowToInstallModalProps> = ({
  isOpen,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">How to Install</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* iPhone (Safari) Section */}
            <div className="bg-white border border-gray-200 rounded-2xl p-4 h-full">
              <div className="flex items-start gap-3 mb-3">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-xl bg-blue-50 text-2xl">
                  📱
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-0.5">iPhone (Safari)</h3>
                  <p className="text-sm text-gray-600">Best experience using Safari.</p>
                </div>
              </div>

              <ol className="text-sm text-gray-700 space-y-1.5 pl-6 mb-0">
                <li>
                  <strong>Open:</strong> AACore Booking in <strong>Safari</strong>.
                </li>
                <li>
                  <strong>Tap:</strong> the <strong>…</strong> in the bottom right.
                </li>
                <li>
                  <strong>Tap:</strong> <strong>Share</strong>.
                </li>
                <li>
                  <strong>Scroll:</strong> and select <strong>"Add to Home Screen"</strong>.
                </li>
                <li>
                  <strong>Optional:</strong> Edit the name if you choose.
                </li>
                <li>
                  <strong>Tap:</strong> <strong>"Add"</strong>.
                </li>
              </ol>

              <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-2">
                <p className="text-sm text-gray-600 mb-0">
                  Note: Please leave <strong>"Open as Web App"</strong> set to <strong>On</strong>.
                </p>
              </div>
            </div>

            {/* Android Section */}
            <div className="bg-white border border-gray-200 rounded-2xl p-4 h-full">
              <div className="flex items-start gap-3 mb-3">
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-xl bg-green-50 text-2xl">
                  🤖
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-0.5">Android</h3>
                  <p className="text-sm text-gray-600">Chrome and Edge supported.</p>
                </div>
              </div>

              <div className="mb-3">
                <div className="font-semibold text-sm text-gray-900 mb-2">Chrome Browser</div>
                <ol className="text-sm text-gray-700 space-y-1.5 pl-6 mb-3">
                  <li>
                    <strong>Open:</strong> AACore Booking in <strong>Chrome</strong>.
                  </li>
                  <li>
                    <strong>Tap:</strong> the vertical <strong>⋮</strong> (three dots).
                  </li>
                  <li>
                    <strong>Select:</strong> <strong>"Add to Home Screen"</strong>.
                  </li>
                </ol>
              </div>

              <div>
                <div className="font-semibold text-sm text-gray-900 mb-2">Edge Browser</div>
                <ol className="text-sm text-gray-700 space-y-1.5 pl-6 mb-0">
                  <li>
                    <strong>Open:</strong> AACore Booking in <strong>Edge</strong>.
                  </li>
                  <li>
                    <strong>Tap:</strong> the hamburger icon (three lines).
                  </li>
                  <li>
                    <strong>Select:</strong> <strong>"Add to phone"</strong>.
                  </li>
                </ol>
              </div>
            </div>
          </div>

          {/* Desktop Section */}
          <div className="mt-4 bg-white border border-gray-200 rounded-2xl p-4">
            <div className="flex items-start gap-3 mb-3">
              <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-xl bg-purple-50 text-2xl">
                💻
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-0.5">Desktop (Windows & macOS)</h3>
                <p className="text-sm text-gray-600">Works on Chrome, Edge, and other Chromium-based browsers.</p>
              </div>
            </div>

            <ol className="text-sm text-gray-700 space-y-1.5 pl-6 mb-0">
              <li>
                <strong>Open:</strong> AACore Booking in your browser.
              </li>
              <li>
                <strong>Look:</strong> at the right side of the address bar for an icon that looks like a
                computer with a down arrow.
              </li>
              <li>
                <strong>Click:</strong> <strong>"Install"</strong> or <strong>"Install App"</strong>.
              </li>
              <li>
                <strong>Confirm:</strong> when prompted.
              </li>
            </ol>
          </div>

          {/* Why Install Section */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-xl bg-yellow-50 text-2xl">
                ✨
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Why install?</h3>
                <ul className="text-sm text-gray-700 space-y-1.5 mb-0 pl-5">
                  <li>
                    <strong>Faster access:</strong> open in one tap or click.
                  </li>
                  <li>
                    <strong>App-like experience:</strong> runs in its own window.
                  </li>
                  <li>
                    <strong>Cleaner interface:</strong> less browser clutter.
                  </li>
                  <li>
                    <strong>Convenience:</strong> no need to type the website each time.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors flex items-center gap-2 justify-center"
          >
            <X className="w-4 h-4" />
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
